/**
 * Daily Summary — a once-per-day briefing modal. Auto-opens on the first app
 * load of each calendar day (see MainLayout's trigger) and is reopenable from
 * the "D" button next to the theme picker in the header.
 *
 * v1 sections: important unread emails the user may have missed, tasks due
 * today, and today's calendar events. (A personalized news section is planned
 * for a follow-up once a real user-interests source exists.)
 */
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { useConversation } from '@/app/providers/ConversationContext';
import { useModulePane } from '@/app/providers/ModulePaneContext';
import { useObjectSelection } from '@/app/providers/ObjectSelectionContext';
import { useActivitiesTab } from '@/features/activities/useActivities';
import { useObjectsByType, type XaviorObjectBase } from '@/features/objects/hooks';
import { getEmailStatus, getCachedInbox, listDrafts } from '@/lib/api/endpoints/email-module';
import { toRecipientList } from '@/modules/email/utils/senderParsing';
import { getNewsBriefing } from '@/lib/api/endpoints/news';
import { listWorkspaceModules } from '@/lib/api/endpoints/workspaces';
import { resolveWorkspaceId, buildConversationPath } from '@/lib/workspace-routing';
import { queryKeys } from '@/lib/query/keys';
import type { EventObject } from '@/modules/calendar/eventTypes';
import { computeSuspectedDuplicateIds } from '@/modules/calendar/lib/calendarEventFilters';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { CHAT_DOCK_LANDING_REVEAL_MS } from '@/common/motion/chatDockMotion';
import type { BriefDockState } from '@/app/providers/BriefDockContext';
import './DailySummaryModal.css';

/** Time-of-day greeting (local hour). */
function greetingForHour(h: number): string {
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 14) return 'Good to see you';
  if (h >= 14 && h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** Minimal task shape the summary reads (TaskObject's `version` is optional, so
 *  it doesn't satisfy useObjectsByType's XaviorObjectBase constraint). */
type SummaryTask = XaviorObjectBase & {
  title?: string;
  status?: 'todo' | 'in_progress' | 'done';
  priority?: 'low' | 'medium' | 'high';
  dueAt?: string;
  moduleId?: string;
  moduleIds?: string[];
};

// Importance ranking for the "important emails" section. The email pipeline
// already classifies each message (@xavior/shared mail-classifier) into a
// category; we map that to a weight matching the user's hierarchy: people you
// know + work mail + backend-flagged "important" (which includes payment/
// billing alerts) rank highest. Notifications, newsletters and promotions are
// treated as low-value and excluded from this section entirely.
const CATEGORY_WEIGHT: Record<string, number> = {
  important: 100,
  people_personal: 90,
  people_work: 80,
};
const UNCLASSIFIED_WEIGHT = 60; // not yet classified — show, but below known people/work
const LOW_VALUE_CATEGORIES = new Set(['notification', 'newsletter', 'promotion']);

/** Max items shown per Daily Summary section — only the most important survive. */
const MAX_ITEMS = 3;

/** Max news updates shown in the brief — keep it skimmable, not cluttered. */
const MAX_NEWS = 4;

/** Matches the "no coverage found"-style non-stories the briefing LLM sometimes
 *  emits for genuinely quiet topics — filtered out so they never render. */
const NEWS_PLACEHOLDER_RE =
  /\bno (recent |current )?(coverage|news|updates?|stories|results?|developments?)\b|not found|nothing (found|to report)/i;

// ── Daily Brief preferences (localStorage, xavior:-namespaced) ───────────────
const LS_AUTO_OPEN = 'xavior:dailyBrief:autoOpen';
const LS_NEWS_TOPICS = 'xavior:dailyBrief:newsTopics';

/** Raw per-browser auto-open preference: true = on, false = off, null = unset. */
export function getDailyBriefAutoOpenPref(): boolean | null {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(LS_AUTO_OPEN);
  return v === '1' ? true : v === '0' ? false : null;
}

/** Persist an explicit auto-open choice. */
export function setDailyBriefAutoOpen(on: boolean): void {
  if (typeof window !== 'undefined') localStorage.setItem(LS_AUTO_OPEN, on ? '1' : '0');
}

/**
 * Whether the brief should auto-open once a day. An explicit choice always
 * wins — so a user who turned it ON gets the brief even before finishing
 * onboarding. With no explicit choice, it defaults ON for onboarded users and
 * OFF for brand-new accounts (so first-time users aren't greeted by it).
 */
export function readDailyBriefAutoOpen(onboardingCompleted: boolean): boolean {
  return getDailyBriefAutoOpenPref() ?? Boolean(onboardingCompleted);
}
function readNewsTopics(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LS_NEWS_TOPICS);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Object type → where it shows up, for the "X updated" confirmation after a note.
 *  Keyed off the create_object list-invalidation the agent fires (objects.byType). */
const TYPE_DESTINATION: Record<string, string> = {
  event: 'Calendar',
  task: 'Tasks',
  note: 'Notes',
  contact: 'Contacts',
  place: 'Places',
  asset: 'Drive',
  stat: 'Stats',
};

/** "Calendar", "Calendar and Tasks", "Calendar, Tasks and Notes". */
function joinDestinations(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Best-effort guess of whether a note is a calendar/task ADD request — used only
 * to pick the loading label ("Adding…" vs the neutral "Thinking…"). The agent
 * still decides what actually happens; this just avoids saying "Adding" when the
 * note is clearly a question. Errs toward "Thinking…" when unsure.
 */
function looksLikeAddRequest(note: string): boolean {
  const t = note.toLowerCase();
  // A leading question word / "?" reads as a question, not an add.
  if (/\?\s*$/.test(t) || /^(what|who|when|where|why|how|is|are|do|does|can|should|tell me)\b/.test(t)) {
    return false;
  }
  const addVerb = /\b(add|schedule|create|set up|set a|book|plan|remind me|remember to|make a|put)\b/.test(t);
  const itemNoun = /\b(meeting|event|appointment|task|reminder|to-?do|call|lunch|dinner|deadline|birthday|flight|interview)\b/.test(t);
  const timeCue = /\b(today|tomorrow|tonight|monday|tuesday|wednesday|thursday|friday|saturday|sunday|next week|am|pm|o'?clock|at \d)\b/.test(t);
  return addVerb || itemNoun || (timeCue && !/\bnews\b/.test(t));
}

/** Lower = more important. Used to rank tasks / attention items by priority. */
const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 0, medium: 1, normal: 1, low: 2 };
function priorityRank(p?: string): number {
  return PRIORITY_RANK[(p ?? '').toLowerCase()] ?? 1;
}

/** Short "why it's here" label for an email's classification. */
function categoryLabel(category?: string): string {
  switch (category) {
    case 'important':
      return 'important';
    case 'people_work':
      return 'work';
    case 'people_personal':
      return 'personal';
    default:
      return '';
  }
}


/** Best-effort person name from a birthday event title ("Jane's Birthday" → "Jane"). */
function birthdayName(title: string): string {
  const cleaned = title
    .replace(/['’]s\s+birthday/i, '')
    .replace(/\bbirthday\b/i, '')
    .replace(/🎂/g, '')
    .replace(/[:\-–]\s*$/, '')
    .trim();
  return cleaned || title;
}

/** True when an ISO/date string falls on the local "today". */
function isToday(dateStr?: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** True when a date falls on a day BEFORE today (i.e. overdue at day granularity). */
function isBeforeToday(dateStr?: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return d < startOfToday;
}

export function DailySummaryModal({
  onClose,
  isOpen = true,
  mode = 'modal',
  dockState,
  cardRef,
  landingReveal = false,
  onLandingRevealComplete,
}: {
  onClose: () => void;
  /** Whether the brief is open — drives the landing focus + body chrome lock. */
  isOpen?: boolean;
  /** 'modal' (default) = centered overlay + backdrop (mobile / narrow fallback).
   *  'panel' = inline docked companion card, NO portal/backdrop — the parent
   *  positions it (desktop solo-chat right-side dock, or split-view stack). */
  mode?: 'modal' | 'panel';
  /** Dock state machine phase (solo companion only) — drives the dock/undock
   *  fade classes; absent in the deferred (split/modal) placements. */
  dockState?: BriefDockState;
  /** Ref to the panel card root so the parent (which owns the dock rect
   *  providers) can measure the flight's resting rect. */
  cardRef?: RefObject<HTMLDivElement | null>;
  /** True while the post-open landing window is live — the flight already
   *  supplied the motion, so the card reveals with a plain fade. */
  landingReveal?: boolean;
  /** Called once the landing reveal window has elapsed (acks the one-shot). */
  onLandingRevealComplete?: () => void;
}) {
  const { token, profile, selectedContainer } = useSession();
  const { activeConversation, allConversations } = useConversation();
  const { selectObject } = useObjectSelection();
  // Pane authority (ADR 028) — openItem records intent before navigating.
  const { openPane } = useModulePane();
  const navigate = useNavigate();
  const workspaceId = resolveWorkspaceId(selectedContainer);

  // ── Dock / panel-mode plumbing (mirrors the chat dock) ───────────────────
  // Close button — focus target when the open flight lands (opening the panel
  // moves focus into it, mirroring the chat dock's pendingOpenFocus).
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  // Hand the card root to the parent's measurement ref (panel mode only).
  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (cardRef) cardRef.current = node;
    },
    [cardRef],
  );
  // Close on ESC (both modes).
  useEscapeKey(onClose, isOpen);
  useEffect(() => {
    if (landingReveal) closeButtonRef.current?.focus();
  }, [landingReveal]);
  // Retire the post-open landing window after the reveal has played out, so the
  // card drops .brief-dock-landing back to its resting style.
  useEffect(() => {
    if (!landingReveal || !onLandingRevealComplete) return;
    const t = window.setTimeout(onLandingRevealComplete, CHAT_DOCK_LANDING_REVEAL_MS);
    return () => window.clearTimeout(t);
  }, [landingReveal, onLandingRevealComplete]);
  // Hide mobile chrome when open — modal mode only (the panel is an inline
  // companion, not a full overlay).
  useEffect(() => {
    if (isOpen && mode === 'modal') {
      document.body.classList.add('modal-open');
      return () => document.body.classList.remove('modal-open');
    }
  }, [isOpen, mode]);
  // Calendar-style auto-hide scrollbar: the body carries the shared `.xv-scroll`
  // class (common/styles/scrollbars.css) and the app-wide controller in AppContent
  // (app/App.tsx, useGlobalAutoHideScrollbars) toggles `.is-scrolling` while
  // scrolling — so the thumb appears on scroll and sheds on idle on every OS/theme,
  // with no bespoke per-component timer or scroll-driven re-render.

  // Module list — used to resolve which module to open when an item is clicked.
  const modulesQ = useQuery({
    queryKey: queryKeys.modules.list(workspaceId),
    queryFn: () => listWorkspaceModules(token, workspaceId),
    enabled: !!token && !!workspaceId,
    staleTime: 60_000,
  });
  const findModuleId = (types: string[]): string | undefined =>
    modulesQ.data?.find((m) => !m.archivedAt && types.includes(m.type))?.id;

  // Brief actions navigate the workspace BEHIND the panel. The docked panel
  // STAYS OPEN — the user closes it themselves (X / bell / Escape). Only the
  // centered MODAL (mobile / narrow fallback) closes after an action: it's a
  // blocking overlay, so navigating underneath it would be invisible. (The
  // unconditional close was a leftover from the Brief's modal-only days.)
  const closeAfterAction = () => {
    if (mode === 'modal') onClose();
  };

  // Open a module pane and deep-link to a specific object. Mirrors the rail's
  // openModule (navigate into the module within the active conversation + nudge
  // it to focus), then uses the cross-module object-selection bus so the module
  // opens/highlights the exact item. The selection persists in context until
  // the (now-mounting) module's listener handles it, so order vs. mount doesn't
  // matter.
  const openItem = (
    moduleId: string | undefined,
    sel?: { type: 'email' | 'task' | 'event'; id?: string; meta?: Record<string, unknown> },
  ) => {
    const convId = activeConversation?.id ?? allConversations[0]?.id;
    if (!moduleId || !workspaceId || !convId) return;
    // SSOT (ADR 028): record the open intent in the pane authority first, so
    // the URL guard holds for a not-yet-attached module and background
    // hydration can't overwrite this click.
    openPane(moduleId);
    navigate(buildConversationPath(workspaceId, convId, { moduleId }));
    window.dispatchEvent(new CustomEvent('xavior:force-module-focus', { detail: { moduleId } }));
    // `meta` carries extra context (e.g. an event's occurrence date) and
    // persists with the selection until the target module mounts and handles it.
    if (sel?.id) selectObject(sel.type, sel.id, sel.meta);
    closeAfterAction();
  };

  // ── Needs your attention — the cron "deliver" output (proactive follow-ups,
  // stale-activity & cold-contact nudges, drafts to review) lands in the
  // activity feed's review tab. We surface the top few here; clicking one opens
  // the conversation it relates to (when it has one). ────────────────────────
  const reviewQ = useActivitiesTab(workspaceId, 'review', Boolean(token && workspaceId));
  const attentionItems = useMemo(
    () =>
      [...reviewQ.items]
        .sort(
          (a, b) =>
            priorityRank(a.priority) - priorityRank(b.priority) ||
            new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
        )
        .slice(0, MAX_ITEMS),
    [reviewQ.items],
  );
  const openAttentionItem = (conversationId?: string) => {
    if (!conversationId || !workspaceId) return;
    navigate(buildConversationPath(workspaceId, conversationId));
    closeAfterAction();
  };

  // ── Important emails (best-effort) ───────────────────────────────────────
  const emailStatusQ = useQuery({
    queryKey: ['daily-summary', 'email-status'],
    queryFn: () => getEmailStatus(undefined, token),
    enabled: !!token,
    staleTime: 60_000,
  });
  const accountIds = useMemo(
    () => (emailStatusQ.data?.accounts ?? []).map((a) => a.id),
    [emailStatusQ.data],
  );
  const emailConnected = Boolean(emailStatusQ.data?.connected) && accountIds.length > 0;
  const inboxQ = useQuery({
    queryKey: ['daily-summary', 'inbox', accountIds.join(',')],
    queryFn: () =>
      getCachedInbox(
        { limit: 40, offset: 0, accountIds: accountIds.map(encodeURIComponent).join(',') },
        token,
      ),
    enabled: !!token && emailConnected,
    staleTime: 60_000,
  });
  const importantEmails = useMemo(() => {
    return (inboxQ.data?.messages ?? [])
      .filter((m) => m.isUnread && !LOW_VALUE_CATEGORIES.has(m.category ?? ''))
      .map((m) => ({ m, score: CATEGORY_WEIGHT[m.category ?? ''] ?? UNCLASSIFIED_WEIGHT }))
      // Highest importance first; within a tier, most recent first.
      .sort(
        (a, b) =>
          b.score - a.score ||
          new Date(b.m.date ?? 0).getTime() - new Date(a.m.date ?? 0).getTime(),
      )
      .slice(0, MAX_ITEMS)
      .map((x) => x.m);
  }, [inboxQ.data]);

  // ── Tasks — overdue (due before today) + due today, both not done ──────────
  const tasksQ = useObjectsByType<SummaryTask>('task', { limit: 200 });
  const taskRows = useMemo(() => {
    const open = (tasksQ.data?.items ?? []).filter((t) => t.status !== 'done');
    // Within each bucket: higher priority first, then more-overdue / sooner-due.
    const byImportance = (a: SummaryTask, b: SummaryTask) =>
      priorityRank(a.priority) - priorityRank(b.priority) ||
      new Date(a.dueAt ?? 0).getTime() - new Date(b.dueAt ?? 0).getTime();
    const overdue = open.filter((t) => isBeforeToday(t.dueAt)).sort(byImportance);
    const today = open.filter((t) => isToday(t.dueAt)).sort(byImportance);
    // Overdue outranks today; cap at MAX_ITEMS so only the most pressing show.
    return [
      ...overdue.map((t) => ({ t, overdue: true })),
      ...today.map((t) => ({ t, overdue: false })),
    ].slice(0, MAX_ITEMS);
  }, [tasksQ.data]);

  // ── Drafts ready (best-effort, email-connected only) ───────────────────────
  const draftsQ = useQuery({
    queryKey: ['daily-summary', 'drafts', accountIds.join(',')],
    queryFn: () =>
      listDrafts(
        { max: 25, accountIds: accountIds.map(encodeURIComponent).join(',') },
        token,
      ),
    enabled: !!token && emailConnected,
    staleTime: 60_000,
  });
  const draftsReady = [...(draftsQ.data?.drafts ?? [])]
    .sort(
      (a, b) =>
        new Date(b.message?.date ?? 0).getTime() - new Date(a.message?.date ?? 0).getTime(),
    )
    .slice(0, MAX_ITEMS);

  // ── Today's events ───────────────────────────────────────────────────────
  const { startISO, endISO } = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString() };
  }, []);
  const eventsQ = useObjectsByType<EventObject>('event', {
    limit: 100,
    startAfter: startISO,
    startBefore: endISO,
  });
  // Today's events, with birthdays split into their own section so they don't
  // double-list.
  const { todayEvents, birthdays, dupIds } = useMemo(() => {
    const all = (eventsQ.data?.items ?? []).filter((e) => isToday(e.startAt));
    const events = all
      .filter((e) => e.eventType !== 'birthday')
      // Timed (scheduled) events first, then all-day; each by soonest start.
      .sort(
        (a, b) =>
          (a.allDay ? 1 : 0) - (b.allDay ? 1 : 0) ||
          new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
      );
    return {
      todayEvents: events.slice(0, MAX_ITEMS),
      birthdays: all.filter((e) => e.eventType === 'birthday').slice(0, MAX_ITEMS),
      // #3952: same-slot suspected-duplicate flag, computed over the FULL day's
      // set (before the MAX_ITEMS slice) so the badge reflects the real day, not
      // the truncated 3 shown. Non-destructive — flag only, the rows are kept.
      // Birthdays are EXCLUDED (use `events`, not `all`): birthday events render
      // in their own section and never carry a badge, so feeding them to the
      // grouper would falsely flag a same-slot regular event (e.g. "Vincent"
      // 09:00–10:00) as a duplicate of "Vincent's Birthday" with no visible
      // counterpart to compare against.
      dupIds: computeSuspectedDuplicateIds(events),
    };
  }, [eventsQ.data]);

  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const firstName = (profile?.name ?? '').trim().split(' ')[0];
  const greeting = greetingForHour(new Date().getHours());

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  // Bottom chat box — hand a note about the day to Xavior (active conversation).
  // Xavior interprets it and acts (creating tasks/events/etc. via its tools). We
  // keep the summary OPEN and tell the user EXACTLY where it landed by watching
  // the agent's create_object list-invalidations (objects.byType) — that same
  // invalidation also refreshes the modal's data automatically.
  // Bottom box — a self-contained quick-capture (NOT the Xavior chat). The note
  // is handed to the agent silently (hidden user message + hidden response). The
  // agent either creates the calendar event/task directly, or — if a detail like
  // the date/time is missing — replies with ONE short question, which we surface
  // HERE as a follow-up (never in the chat thread). Answering it re-sends with
  // the extra detail. Outcomes: detected creation → "Calendar updated"; a
  // question → in-modal follow-up; otherwise → "Noted."
  const [dayNote, setDayNote] = useState('');
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [followUpQuestion, setFollowUpQuestion] = useState<string | null>(null);
  const [discussTopic, setDiscussTopic] = useState<string | null>(null);
  const pendingNoteRef = useRef('');

  // ── Settings (three-dot menu): auto-open toggle + news-topic management ─────
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'general' | 'news'>('general');
  // Switch reflects the EFFECTIVE state: explicit pref, else default (onboarded
  // → on, new account → off). Toggling persists an explicit choice (below).
  const [autoOpen, setAutoOpen] = useState(
    () => getDailyBriefAutoOpenPref() ?? Boolean(profile?.onboardingCompleted),
  );
  const [newsTopics, setNewsTopics] = useState<string[]>(readNewsTopics);
  const [newTopic, setNewTopic] = useState('');

  // If the modal mounts before the session profile resolves, the lazy default
  // above can be wrong (onboarded user reads as new → switch shows OFF). Re-sync
  // to the effective default once profile loads — but ONLY when the user has no
  // explicit pref, so a real toggle is never clobbered.
  useEffect(() => {
    if (getDailyBriefAutoOpenPref() === null) {
      setAutoOpen(Boolean(profile?.onboardingCompleted));
    }
  }, [profile?.onboardingCompleted]);

  // Persist the auto-open choice only when the user actually toggles it (not on
  // mount) so we never freeze the onboarding-based default into an explicit pref.
  const toggleAutoOpen = () =>
    setAutoOpen((v) => {
      const next = !v;
      setDailyBriefAutoOpen(next);
      return next;
    });

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(LS_NEWS_TOPICS, JSON.stringify(newsTopics));
  }, [newsTopics]);

  const addTopic = () => {
    const t = newTopic.trim();
    if (!t) return;
    setNewsTopics((prev) => (prev.some((x) => x.toLowerCase() === t.toLowerCase()) ? prev : [...prev, t]));
    setNewTopic('');
  };
  const removeTopic = (t: string) => setNewsTopics((prev) => prev.filter((x) => x !== t));

  // Personalized news — fetched from /news/briefing using EXACTLY the user's
  // topics (kept clean: the backend embeds this string into its search query, so
  // anything but the bare topic list pollutes the results). Importance/recency
  // ranking lives in the briefing's synthesis prompt, not here. We then surface
  // the top MAX_NEWS. Cached (news moves slowly) and only runs when topics exist.
  const newsInstructions = newsTopics.join(', ');
  const newsQ = useQuery({
    queryKey: ['daily-brief', 'news', newsInstructions],
    queryFn: () => getNewsBriefing(newsInstructions, token),
    enabled: Boolean(token && newsTopics.length > 0),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  });
  // Drop "no coverage found" placeholder stories the model occasionally emits for
  // genuinely quiet topics — better to show the honest empty state than a fake
  // headline. Real topics are unaffected; we then surface up to MAX_NEWS.
  const newsStories = (newsQ.data?.stories ?? [])
    .filter((s) => s.headline && !NEWS_PLACEHOLDER_RE.test(s.headline))
    .slice(0, MAX_NEWS);
  const detectRef = useRef<{ cleanup?: () => void; timer?: ReturnType<typeof setTimeout> }>({});
  useEffect(
    () => () => {
      detectRef.current.cleanup?.();
      if (detectRef.current.timer) clearTimeout(detectRef.current.timer);
    },
    [],
  );

  const submitDayNote = () => {
    const text = dayNote.trim();
    if (!text) return;
    const answering = followUpQuestion !== null;
    // When answering a follow-up, fold the clarification into the running note.
    const note = answering ? `${pendingNoteRef.current} — ${text}` : text;
    // Persist the COMBINED note so a second follow-up accumulates onto the first
    // answer instead of rebuilding from the original and dropping it.
    pendingNoteRef.current = note;

    const todayStr = new Date().toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const prompt =
      `A note from my daily summary: "${note}". Today is ${todayStr}. My timezone is ${tz} — ALWAYS ` +
      `create/amend events in MY timezone, and whenever you look a time up, CONVERT it to ${tz} first ` +
      `(e.g. a game listed at 5:30 PM ET must be converted to my local ${tz} time before creating — ` +
      `getting the timezone wrong is a bug). Resolve relative dates ("tomorrow"/"next week") from ` +
      `today. Do EXACTLY ONE of the following, and never respond with nothing:\n` +
      `1) Clear date AND start time → create the calendar event (a clear task → create the task). If ` +
      `no end time/duration is given, do NOT ask — estimate a sensible duration for that kind of ` +
      `event (coffee ~30m, lunch ~1h, meeting/appointment ~1h, dinner ~1.5h, gym ~1h, quick call ~30m).\n` +
      `2) No start time but it's a well-known, publicly-scheduled event (a televised sports game like ` +
      `an NBA Finals game, a concert, launch, awards show, broadcast) → use web search to find its ` +
      `real start time for that date, CONVERT it to ${tz}, and create the event — do NOT ask for the time.\n` +
      `3) AMENDING an existing event (reschedule, move to another day, rename, change time/location — ` +
      `e.g. "move my dentist to 3pm", "the game is actually tomorrow") → find that event and UPDATE it ` +
      `(update_object); do NOT create a duplicate.\n` +
      `4) If what I said conflicts with what you find for a public event (I gave the wrong day, time, ` +
      `or opponent) → do NOT silently create and do NOT silently "fix" it; reply with ONE short ` +
      `question to confirm the correction (e.g. "That game is Wednesday, not Tuesday — add it then?").\n` +
      `5) If a needed detail is still missing/ambiguous — the START time of a PERSONAL event you ` +
      `cannot look up, which day, or what the item is — reply with ONE short question for just that ` +
      `detail. (A missing END time/duration is NEVER a reason to ask — infer per rule 1.)\n` +
      `6) If it is NOT an action — a question/topic to learn about (e.g. "what's up with bitcoin") → ` +
      `reply with exactly "DISCUSS: <topic in 1-4 words>".`;
    window.dispatchEvent(
      new CustomEvent('xavior:send-chat-message', {
        detail: { message: prompt, options: { hideUserMessage: true, hideAssistantResponse: true } },
      }),
    );
    setDayNote('');
    setFollowUpQuestion(null);
    setDiscussTopic(null);
    // Say "Adding…" only when we're fairly sure it's an add request (or we're
    // answering a clarifying question, which is always an add); otherwise stay
    // neutral with "Thinking…".
    setConfirmation(answering || looksLikeAddRequest(note) ? 'Adding…' : 'Thinking…');

    detectRef.current.cleanup?.();
    if (detectRef.current.timer) clearTimeout(detectRef.current.timer);

    // The agent's hidden turn reports back what it ACTUALLY created
    // (createdTypes) plus any reply text. We name the destination ONLY from
    // those real creations; otherwise the reply is either a clarifying question
    // or a "DISCUSS:" marker (a topic to open the chat about).
    const onSilentReply = (ev: Event) => {
      const detail = (ev as CustomEvent).detail ?? {};
      const createdTypes: string[] = Array.isArray(detail.createdTypes) ? detail.createdTypes : [];
      const reply = String(detail.text ?? '').trim();
      const names = [...new Set(createdTypes.map((t) => TYPE_DESTINATION[t]).filter(Boolean))];
      const discussMatch = reply.match(/^DISCUSS:\s*(.+)$/i);
      if (names.length > 0) {
        setConfirmation(`${joinDestinations(names)} updated`);
      } else if (discussMatch) {
        // It's a question/topic, not an action — offer to open the chat about it.
        setConfirmation(null);
        setDiscussTopic(discussMatch[1].trim().replace(/[.?!]+$/, '') || 'this');
      } else if (/\?\s*["'’”)\]]*$/.test(reply)) {
        // Nothing created and it's asking for a detail — surface it here.
        setConfirmation(null);
        setFollowUpQuestion(reply);
      } else {
        setConfirmation('Noted.');
      }
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('xavior:silent-response', onSilentReply as EventListener);
      if (detectRef.current.timer) clearTimeout(detectRef.current.timer);
      detectRef.current = {};
    };

    window.addEventListener('xavior:silent-response', onSilentReply as EventListener);
    detectRef.current.cleanup = cleanup;
    // Fallback if the turn never reports back.
    detectRef.current.timer = setTimeout(() => {
      setConfirmation('Noted.');
      cleanup();
    }, 15000);
  };

  if (!isOpen) return null;

  // Every section hides itself when empty; when ALL are, the body shows one
  // friendly all-clear line instead. Gated on the section queries having
  // settled so it never flashes while data is still loading. (Disabled queries
  // — e.g. inbox/drafts with no email account — report isLoading false.)
  const sectionsLoading =
    reviewQ.isLoading ||
    inboxQ.isLoading ||
    draftsQ.isLoading ||
    tasksQ.isLoading ||
    eventsQ.isLoading ||
    (newsTopics.length > 0 && newsQ.isLoading);
  const briefEmpty =
    attentionItems.length === 0 &&
    importantEmails.length === 0 &&
    draftsReady.length === 0 &&
    taskRows.length === 0 &&
    todayEvents.length === 0 &&
    birthdays.length === 0 &&
    newsStories.length === 0;

  // Dock/undock fade classes (solo companion only): the closing flight fades the
  // content out under the traveling glass; the landing window swaps the entrance
  // keyframe for a plain fast fade (the flight supplied the motion).
  const dockClass = [
    dockState === 'docking' ? ' brief-dock-docking' : '',
    dockState === 'undocking' ? ' brief-dock-undocking' : '',
    landingReveal ? ' brief-dock-landing' : '',
  ].join('');

  const card = (
      <div
        ref={setCardRef}
        id="daily-brief-panel"
        role="dialog"
        aria-modal={mode === 'modal' ? true : undefined}
        aria-label="Daily Brief"
        className={`daily-summary-modal${mode === 'panel' ? ' daily-summary-panel-card' : ''}${dockClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — visual clone of the module pane header (.chat-module-pane-header
            in ChatBox.css): same 60px height, separator line, title scale, and the
            exact 36px / 16px-icon action buttons. KEEP IN SYNC with that spec. */}
        <div className="daily-summary-header">
          <div className="daily-summary-header-left">
            <h2 className="daily-summary-title">
              {settingsOpen ? 'Daily Briefing settings' : `Daily Briefing - ${todayLabel}`}
            </h2>
          </div>
          <div className="daily-summary-header-right">
            <button
              type="button"
              className={`daily-summary-menu${settingsOpen ? ' active' : ''}`}
              onClick={() => setSettingsOpen((v) => !v)}
              title="Daily Brief settings"
              aria-label="Daily Brief settings"
              aria-pressed={settingsOpen}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
                <circle cx="5" cy="12" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="19" cy="12" r="2" />
              </svg>
            </button>
            <button
              ref={closeButtonRef}
              type="button"
              className="daily-summary-close"
              onClick={onClose}
              title="Close"
              aria-label="Close daily summary"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {settingsOpen ? (
          <div className="daily-summary-settings">
            <div className="db-settings-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={settingsTab === 'general'}
                className={`db-settings-tab${settingsTab === 'general' ? ' active' : ''}`}
                onClick={() => setSettingsTab('general')}
              >
                General
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={settingsTab === 'news'}
                className={`db-settings-tab${settingsTab === 'news' ? ' active' : ''}`}
                onClick={() => setSettingsTab('news')}
              >
                News
              </button>
            </div>

            {settingsTab === 'general' ? (
              <div className="db-settings-pane">
                <div className="db-toggle-row">
                  <div className="db-toggle-text">
                    <span className="db-toggle-label">Open automatically each day</span>
                    <span className="db-toggle-sub">
                      Show the Daily Brief on your first visit each day. Off means it only opens
                      from the bell.
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autoOpen}
                    aria-label="Open the Daily Brief automatically each day"
                    className={`db-switch${autoOpen ? ' on' : ''}`}
                    onClick={toggleAutoOpen}
                  >
                    <span className="db-switch-knob" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="db-settings-pane">
                <p className="db-news-help">
                  Follow as many topics as you like. Your brief shows the {MAX_NEWS} most important
                  and timely updates each day.
                </p>
                <form
                  className="db-news-add"
                  onSubmit={(e) => {
                    e.preventDefault();
                    addTopic();
                  }}
                >
                  <input
                    className="db-news-input"
                    type="text"
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    placeholder="e.g. Bitcoin, the NBA, AI policy"
                    aria-label="Add a news topic"
                  />
                  <button type="submit" className="db-news-add-btn" disabled={!newTopic.trim()}>
                    Add
                  </button>
                </form>
                <div className="db-news-chips">
                  {newsTopics.length === 0 ? (
                    <span className="db-news-empty">No topics yet — add one above.</span>
                  ) : (
                    newsTopics.map((t) => (
                      <span className="db-news-chip" key={t}>
                        {t}
                        <button
                          type="button"
                          className="db-news-chip-x"
                          aria-label={`Remove ${t}`}
                          title={`Remove ${t}`}
                          onClick={() => removeTopic(t)}
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
        <div
          className="daily-summary-body xv-scroll"
        >
          {/* Whole-brief empty state — every section hides itself when empty,
              so when ALL of them are, this friendly line is the only content.
              Gated on the queries settling so it never flashes during load. */}
          {briefEmpty && !sectionsLoading && (
            <div className="daily-summary-all-clear">
              {greeting}
              {firstName ? `, ${firstName}` : ''} — nothing in your brief today.
              You&rsquo;re all caught up.
            </div>
          )}
          {/* Needs your attention (cron-delivered: follow-ups, nudges, drafts).
              Hidden entirely when empty. */}
          {attentionItems.length > 0 && (
            <section className="daily-summary-section">
              <h3 className="daily-summary-section-title">Needs your attention</h3>
              <div className="daily-summary-list">
                {attentionItems.map((it) => {
                  const main = it.title || it.description || 'Needs attention';
                  const sub = it.title && it.description ? it.description : null;
                  // Clickable when it links to a conversation; otherwise a plain
                  // (non-interactive) informational row.
                  return it.conversationId ? (
                    <button
                      key={it.id}
                      type="button"
                      className="daily-summary-item"
                      onClick={() => openAttentionItem(it.conversationId)}
                      title="Open conversation"
                    >
                      <span className="daily-summary-item-main">{main}</span>
                      {sub ? <span className="daily-summary-item-sub">{sub}</span> : null}
                    </button>
                  ) : (
                    <div key={it.id} className="daily-summary-item daily-summary-item--static">
                      <span className="daily-summary-item-main">{main}</span>
                      {sub ? <span className="daily-summary-item-sub">{sub}</span> : null}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Important emails — hidden when empty, like every other section
              (the all-clear line above covers the nothing-at-all case). */}
          {importantEmails.length > 0 && (
            <section className="daily-summary-section">
              <h3 className="daily-summary-section-title">Important emails</h3>
              <div className="daily-summary-list">
                {importantEmails.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className="daily-summary-item"
                    onClick={() =>
                      openItem(findModuleId(['email']), { type: 'email', id: m.threadId ?? m.id })
                    }
                    title="Open in Email"
                  >
                    <span className="daily-summary-item-main">{m.subject || '(no subject)'}</span>
                    <span className="daily-summary-item-sub">
                      {m.fromName || m.fromEmail || 'Unknown sender'}
                      {categoryLabel(m.category) ? ` · ${categoryLabel(m.category)}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Drafts ready — pre-generated replies waiting to send. Hidden when empty. */}
          {draftsReady.length > 0 && (
            <section className="daily-summary-section">
              <h3 className="daily-summary-section-title">Drafts ready</h3>
              <div className="daily-summary-list">
                {draftsReady.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className="daily-summary-item"
                      onClick={() =>
                        openItem(
                          findModuleId(['email']),
                          d.message?.threadId
                            ? { type: 'email', id: d.message.threadId }
                            : undefined,
                        )
                      }
                      title="Open in Email"
                    >
                      <span className="daily-summary-item-main">
                        {d.message?.subject || '(no subject)'}
                      </span>
                      {toRecipientList(d.message?.to).length > 0 ? (
                        <span className="daily-summary-item-sub">To {toRecipientList(d.message?.to).join(', ')}</span>
                      ) : null}
                    </button>
                ))}
              </div>
            </section>
          )}

          {/* Tasks — overdue (flagged) + due today. Hidden when empty. */}
          {taskRows.length > 0 && (
          <section className="daily-summary-section">
            <h3 className="daily-summary-section-title">Tasks</h3>
              <div className="daily-summary-list">
                {taskRows.map(({ t, overdue }) => (
                  <button
                    key={t.id}
                    type="button"
                    className="daily-summary-item"
                    onClick={() =>
                      openItem(
                        t.moduleId ?? t.moduleIds?.[0] ?? findModuleId(['task_list', 'kanban']),
                        { type: 'task', id: t.id },
                      )
                    }
                    title="Open in Tasks"
                  >
                    <span className="daily-summary-item-main">{t.title || '(untitled task)'}</span>
                    {overdue ? (
                      <span className="daily-summary-item-sub daily-summary-item-sub--overdue">
                        overdue
                      </span>
                    ) : t.status === 'in_progress' ? (
                      <span className="daily-summary-item-sub">in progress</span>
                    ) : null}
                  </button>
                ))}
              </div>
          </section>
          )}

          {/* Events — hidden when empty. */}
          {todayEvents.length > 0 && (
          <section className="daily-summary-section">
            <h3 className="daily-summary-section-title">Today&rsquo;s events</h3>
              <div className="daily-summary-list">
                {todayEvents.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className="daily-summary-item daily-summary-item--event"
                    onClick={() =>
                      openItem(findModuleId(['event_list', 'calendar']), {
                        type: 'event',
                        id: e.id,
                        meta: { dateISO: e.startAt },
                      })
                    }
                    title="Open in Calendar"
                  >
                    <span className="daily-summary-item-time">
                      {e.allDay ? 'All day' : fmtTime(e.startAt)}
                    </span>
                    <span className="daily-summary-item-main">{e.title || '(untitled event)'}</span>
                    {dupIds.has(e.id) ? (
                      <span
                        className="daily-summary-item-sub daily-summary-item-sub--dup"
                        title="Another event shares this exact time slot with a similar title — check if it's a duplicate."
                      >
                        possible duplicate
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
          </section>
          )}

          {/* Birthdays — only rendered when there's actually a birthday today. */}
          {birthdays.length > 0 && (
            <section className="daily-summary-section">
              <h3 className="daily-summary-section-title">Birthdays</h3>
              <div className="daily-summary-list">
                {birthdays.map((b) => {
                  const name = birthdayName(b.title || '');
                  return (
                    <div key={b.id} className="daily-summary-bday-row">
                      <span className="daily-summary-item-main">🎂 {name}</span>
                      <button
                        type="button"
                        className="daily-summary-bday-btn"
                        onClick={() => {
                          window.dispatchEvent(
                            new CustomEvent('xavior:send-chat-message', {
                              detail: { message: `Draft a warm email wishing ${name} a happy birthday.` },
                            }),
                          );
                          closeAfterAction();
                        }}
                      >
                        Draft birthday email
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* News for you — updates on the topics the user added in settings.
              Hidden entirely when no topics are configured. Capped to MAX_NEWS. */}
          {newsTopics.length > 0 && (
            <section className="daily-summary-section">
              <h3 className="daily-summary-section-title">News for you</h3>
              {newsQ.isLoading ? (
                <p className="daily-summary-empty">Fetching the latest…</p>
              ) : newsStories.length === 0 ? (
                <p className="daily-summary-empty">
                  {newsQ.isError ? 'Couldn’t load news right now.' : 'No updates right now.'}
                </p>
              ) : (
                <div className="daily-summary-list">
                  {newsStories.map((s, i) => {
                    const url = s.sources?.[0]?.url;
                    const inner = (
                      <>
                        <span className="daily-summary-item-main">
                          {s.emoji ? `${s.emoji} ` : ''}
                          {s.headline}
                        </span>
                        {s.body ? <span className="daily-summary-item-sub">{s.body}</span> : null}
                      </>
                    );
                    return url ? (
                      <a
                        key={i}
                        className="daily-summary-item"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open story"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div key={i} className="daily-summary-item daily-summary-item--static">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>

        {/* Quick-capture box — turns a note into a calendar event/task. If the
            agent needs a detail, it asks right here (not in the chat). MODAL
            mode only: the docked panel always has the real chat right under it,
            so a second input there is redundant. */}
        {mode === 'modal' && (
        <div className="daily-summary-footer">
          {confirmation === 'Thinking…' || confirmation === 'Adding…' ? (
            <div className="daily-summary-thinking" role="status">
              {confirmation}
            </div>
          ) : confirmation ? (
            <div className="daily-summary-confirm" role="status">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {confirmation}
            </div>
          ) : null}
          {followUpQuestion && (
            <div className="daily-summary-followup" role="status">
              {followUpQuestion}
            </div>
          )}
          {discussTopic && (
            <button
              type="button"
              className="daily-summary-discuss-btn"
              onClick={() => {
                // Open the regular Xavior chat with the question, so the answer
                // is waiting there for the user.
                const question = pendingNoteRef.current.trim();
                if (question) {
                  window.dispatchEvent(
                    new CustomEvent('xavior:send-chat-message', { detail: { message: question } }),
                  );
                }
                closeAfterAction();
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Ask Xavior about {discussTopic}
            </button>
          )}
          <form
            className="daily-summary-chat-form"
            onSubmit={(e) => {
              e.preventDefault();
              submitDayNote();
            }}
          >
            <input
              className="daily-summary-chat-input"
              type="text"
              value={dayNote}
              onChange={(e) => {
                setDayNote(e.target.value);
                if (confirmation) setConfirmation(null);
                if (discussTopic) setDiscussTopic(null);
              }}
              placeholder={
                followUpQuestion
                  ? 'Type your answer…'
                  : 'Got anything for Xavior today — events, news, or questions?'
              }
              aria-label={followUpQuestion ? 'Answer Xavior' : 'Tell Xavior about your day'}
            />
            <button
              type="submit"
              className="daily-summary-chat-send"
              disabled={!dayNote.trim()}
              title="Send to Xavior"
              aria-label="Send to Xavior"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13" />
                <path d="M22 2 15 22l-4-9-9-4 20-7z" />
              </svg>
            </button>
          </form>
        </div>
        )}
        </>
        )}
      </div>
  );

  // Panel mode: inline docked companion card (no portal/backdrop) — the parent
  // positions it (desktop solo dock, or module-open split stack).
  if (mode === 'panel') return card;

  // Modal mode: centered overlay + backdrop (mobile / narrow fallback).
  return createPortal(
    <div className="daily-summary-overlay" role="presentation" onClick={onClose}>
      {card}
    </div>,
    document.body,
  );
}
